// @ts-nocheck
import React from 'react';
import {
  Button,
  Card,
  Grid,
  Typography,
  CardContent,
  Box,
  Paper,
  Divider,
  CardActions,
  CardMedia,
  Container,
  Chip,
  useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BalanceIcon from '@mui/icons-material/Balance';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visuals
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: 40,
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

const Software: React.FC = () => {
  const theme = useTheme();

  // Function to handle button click that opens the URL
  const handleDownloadClick = (url: string) => {
    // Open the URL in a new tab
    window.open(url, '_blank');
  };

  // Software data for better organization
  const softwareData = {
    parcel: [
      {
        title: 'EnVision',
        description: 'Comprehensive parcel dimensioning and weighing software solution.',
        icon: <ViewInArIcon />,
        url: 'https://drive.google.com/drive/folders/1zZppV-WJGQ_e_YmwyeHAgpXFlNqxcXjE?usp=drive_link',
      },
      {
        title: 'EnVision (Cognex Version)',
        description: 'EnVision variant optimized for Cognex camera systems.',
        icon: <CameraAltIcon />,
        url: 'https://drive.google.com/drive/folders/1NhmUnHNk3YGK_V5ItAy_Vw9Ha4TcN2-U?usp=drive_link',
      },
      {
        title: 'OmniCube',
        description: 'Advanced dimensioning solution for parcels.',
        icon: <ViewInArIcon />,
        url: 'https://drive.google.com/drive/folders/1xLDZrijdYsZl_Wa7bqNs4QHOHgIuE2a8?usp=drive_link',
      },
      {
        title: 'LTConnect',
        description: 'Connectivity solution for logistics and transportation systems.',
        icon: <StorageIcon />,
        url: 'https://drive.google.com/drive/folders/1iVPR4bVcaagKepAHt8FAFwY_Lco9KCIp?usp=drive_link',
      },
      {
        title: 'DicomV4',
        description: 'Client parcel shipping with local label generation and tracking',
        icon: <DesktopWindowsIcon />,
        url: 'https://drive.google.com/drive/folders/1ZZ6_kM_r9ugbmcul4rRjkhmmO9fm3jR0?usp=drive_link',
      },
    ],
    freight: [
      {
        title: 'ISynergy Scale System',
        description: 'Integrated weighing system for freight and heavy cargo.',
        icon: <BalanceIcon />,
        url: 'https://drive.google.com/file/d/1gea_6I0K5oQNzy530VTsdG2afkd52Hqh/view?usp=sharing',
      },
      {
        title: 'ISynergy Application Launcher (Kiosk)',
        description: 'Kiosk mode application launcher for terminals. (OLD)',
        icon: <TouchAppIcon />,
        url: 'https://drive.google.com/drive/folders/1jsHb9_ll-6XcTrwfxCXJywYwFP-qQb-i?usp=drive_link',
      },
    ],
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Software Downloads
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Access and download the latest versions of our software solutions for parcel and freight management.
        </Typography>

        {/* Parcel Section */}
        <SectionHeader elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 2, fontSize: 28 }} />
            <Typography variant="h5">Parcel Solutions</Typography>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          {softwareData.parcel.map((software, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StyledCard elevation={3}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <IconWrapper>
                    {software.icon}
                    <Box>
                      <Typography variant="h6" component="div" fontWeight="medium">
                        {software.title}
                      </Typography>
                    </Box>
                  </IconWrapper>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {software.description}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadClick(software.url)}
                    size="medium"
                  >
                    Download
                  </Button>
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>

        {/* Freight Section */}
        <SectionHeader elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BalanceIcon sx={{ mr: 2, fontSize: 28 }} />
            <Typography variant="h5">Freight Solutions</Typography>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          {softwareData.freight.map((software, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StyledCard elevation={3}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <IconWrapper>
                    {software.icon}
                    <Box>
                      <Typography variant="h6" component="div" fontWeight="medium">
                        {software.title}
                      </Typography>
                    </Box>
                  </IconWrapper>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {software.description}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadClick(software.url)}
                    size="medium"
                  >
                    Download
                  </Button>
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Software;